import { type FormEvent, type ReactNode, useState } from 'react';
import { i18n } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { Dialog } from './Dialog.js';
import { formatCents, formatDate } from '../lib/format.js';

const { t } = i18n;

interface BillingLine {
  palletId: string | null;
  description: string;
  days: number | null;
  totalCents: number;
}

interface BillingPreview {
  customerId: string;
  periodFrom: string;
  periodTo: string;
  totalCents: number;
  currency: string;
  lines: BillingLine[];
  warnings: string[];
}

type Stage = 'select' | 'preview' | 'pushed' | 'failed';

export function PushInvoiceDialog({
  customerId,
  onClose,
  onPushed,
}: {
  customerId: string;
  onClose: () => void;
  onPushed: () => void;
}) {
  const today = new Date();
  const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const monthEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0));

  const [stage, setStage] = useState<Stage>('select');
  const [periodFrom, setPeriodFrom] = useState(monthStart.toISOString().slice(0, 10));
  const [periodTo, setPeriodTo] = useState(monthEnd.toISOString().slice(0, 10));
  const [preview, setPreview] = useState<BillingPreview | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [pushedInfo, setPushedInfo] = useState<{
    invoiceNumber?: string;
    invoiceUrl?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function loadPreview(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const data = await api<BillingPreview>(`/customers/${customerId}/billing/preview`, {
        method: 'POST',
        body: { periodFrom, periodTo },
      });
      setPreview(data);
      setStage('preview');
    } catch {
      setError(t('billing.preview.error'));
    } finally {
      setBusy(false);
    }
  }

  async function pushNow() {
    if (!preview) return;
    setError(null);
    setBusy(true);
    try {
      const created = await api<{ batch: { id: string }; warnings: string[] }>(
        `/customers/${customerId}/billing`,
        { method: 'POST', body: { periodFrom, periodTo } },
      );
      const newBatchId = created.batch.id;
      setBatchId(newBatchId);

      const outcome = await api<{
        status: 'pushed' | 'retry_scheduled' | 'failed';
        invoiceNumber?: string;
        invoiceUrl?: string;
        error?: string;
      }>(`/billing/${newBatchId}/push`, { method: 'POST' });

      if (outcome.status === 'pushed') {
        setPushedInfo({ invoiceNumber: outcome.invoiceNumber, invoiceUrl: outcome.invoiceUrl });
        setStage('pushed');
        onPushed();
      } else if (outcome.status === 'retry_scheduled') {
        // Worker will retry — surface that to the operator and close.
        setError(t('billing.push.transient'));
        setStage('failed');
        onPushed();
      } else {
        setError(outcome.error ?? t('billing.push.terminal'));
        setStage('failed');
        onPushed();
      }
    } catch (err) {
      const msg =
        err instanceof ApiError && err.code === 'billing_charge_exists'
          ? t('billing.charge.exists')
          : err instanceof ApiError
            ? err.code
            : t('common.error');
      setError(msg);
      setStage('failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('billing.push.title')} onClose={onClose}>
      {stage === 'select' ? (
        <form onSubmit={(e) => void loadPreview(e)} className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <Field label={t('payment.col.period_from')}>
              <input
                type="date"
                value={periodFrom}
                onChange={(e) => setPeriodFrom(e.target.value)}
                required
                className="w-full rounded border border-border bg-background px-2 py-1.5"
              />
            </Field>
            <Field label={t('payment.col.period_to')}>
              <input
                type="date"
                value={periodTo}
                onChange={(e) => setPeriodTo(e.target.value)}
                required
                className="w-full rounded border border-border bg-background px-2 py-1.5"
              />
            </Field>
          </div>
          {error ? <div className="text-red-600">{error}</div> : null}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-border px-3 py-1.5"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={busy}
              className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
            >
              {busy ? t('common.loading') : t('billing.preview.submit')}
            </button>
          </div>
        </form>
      ) : null}

      {stage === 'preview' && preview ? (
        <div className="space-y-3 text-sm">
          <div className="text-xs text-muted-foreground">
            {formatDate(preview.periodFrom)} – {formatDate(preview.periodTo)}
          </div>
          {preview.warnings.length > 0 ? (
            <ul className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
              {preview.warnings.map((w, i) => (
                <li key={i}>⚠ {w}</li>
              ))}
            </ul>
          ) : null}
          <div className="overflow-x-auto rounded border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr>
                  <th className="px-2 py-1 text-left">{t('billing.col.line')}</th>
                  <th className="px-2 py-1 text-right">{t('billing.col.days')}</th>
                  <th className="px-2 py-1 text-right">{t('billing.col.total')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-2 py-3 text-center text-muted-foreground">
                      {t('billing.preview.empty')}
                    </td>
                  </tr>
                ) : (
                  preview.lines.map((l, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-2 py-1">{l.description}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{l.days ?? '—'}</td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        {formatCents(l.totalCents, preview.currency)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-muted/50 font-medium">
                  <td className="px-2 py-1" colSpan={2}>
                    {t('billing.col.grand_total')}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {formatCents(preview.totalCents, preview.currency)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {error ? <div className="text-red-600">{error}</div> : null}
          <div className="flex items-center gap-2 pt-2">
            <a
              href={`/api/customers/${customerId}/billing/preview.pdf?periodFrom=${periodFrom}&periodTo=${periodTo}`}
              target="_blank"
              rel="noreferrer"
              className={`mr-auto rounded border border-border px-3 py-1.5 hover:bg-muted ${
                preview.lines.length === 0 ? 'pointer-events-none opacity-50' : ''
              }`}
            >
              {t('billing.download_pdf')}
            </a>
            <button
              type="button"
              onClick={() => setStage('select')}
              className="rounded border border-border px-3 py-1.5"
            >
              {t('common.back')}
            </button>
            <button
              type="button"
              onClick={() => void pushNow()}
              disabled={busy || preview.lines.length === 0}
              className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
            >
              {busy ? t('common.loading') : t('billing.push.submit')}
            </button>
          </div>
        </div>
      ) : null}

      {stage === 'pushed' ? (
        <div className="space-y-3 text-sm">
          <div className="text-green-700 dark:text-green-300">{t('billing.push.success')}</div>
          {pushedInfo?.invoiceNumber ? (
            <div className="text-xs text-muted-foreground">
              {t('billing.push.invoice_number')}:{' '}
              <span className="font-mono">{pushedInfo.invoiceNumber}</span>
            </div>
          ) : null}
          {pushedInfo?.invoiceUrl ? (
            <a
              href={pushedInfo.invoiceUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-primary hover:underline break-all"
            >
              {pushedInfo.invoiceUrl}
            </a>
          ) : null}
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded bg-primary text-primary-foreground px-3 py-1.5"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      ) : null}

      {stage === 'failed' ? (
        <div className="space-y-3 text-sm">
          <div className="text-red-600">{error ?? t('billing.push.terminal')}</div>
          {batchId ? (
            <div className="text-xs text-muted-foreground">{t('billing.push.failed_hint')}</div>
          ) : null}
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-border px-3 py-1.5"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}
